import { reaction } from 'mobx'
import type { StreamInterface } from 'tricklejs/dist/types'
import type { StreamSubscriptionActions } from 'tricklejs/dist/stream_subscription'
import { WrappedFirestoreError } from '../firestore-mobx/errors'

const nodeEnv = process.env.NODE_ENV

let counter = 0
export abstract class Cubit<T extends object = object> {
  id: string
  state: T
  isDisposed = false

  static callSuperDispose = Symbol('Calling super.dispose is mandatory')

  disposers: VoidFunction[] = []

  constructor() {
    this.id = Math.random().toString()
    this.state = {} as T
  }

  streamStore: {
    subscription: StreamSubscriptionActions
    stream: StreamInterface<unknown>
    namespace?: string
    name?: string
  }[] = []

  addStream<M>(
    stream: StreamInterface<M>,
    callback: (model: M) => void,
    options: {
      namespace?: string
      name?: string
      onError?: (error: Error) => void
      disableCaptureException?: boolean
    } = {}
  ) {
    let measured = false
    let perfName = ''
    if (nodeEnv === 'development') {
      const line = new Error().stack?.split('\n')[2]?.trim()
      const perfSuffix = options.name || line || `unknown-${counter++}`
      const cubitName = this.constructor.name
      perfName = `${cubitName}:stream:${perfSuffix}`

      performance.mark(perfName + '-start')
    }
    const defaultError = (error: Error) => {
      const isWrappedFirestoreError = error instanceof WrappedFirestoreError
      if (options.disableCaptureException && isWrappedFirestoreError) {
        // mark the error as handled so it is not sent to Sentry
        error.isHandled = true
      }
      // call removeStream to remove the stream from the store
      if (options.name) this.removeStream(options.name)
      // call the passed in error handler if not null
      options.onError?.(error)
    }
    const subscription = stream.listen(
      (data) => {
        if (nodeEnv === 'development' && !measured) {
          performance.mark(perfName + '-stop', {
            detail: {
              cubit: this.constructor.name,
            },
          })
          performance.measure(perfName, perfName + '-start', perfName + '-stop')
          measured = true
        }

        callback(data)
      },
      {
        onError: defaultError,
      }
    )
    this.streamStore.push({
      stream,
      subscription,
      namespace: options.namespace,
      name: options.name,
    })
    return stream
  }

  hasStream(name: string) {
    return this.streamStore.some((entry) => entry.name === name)
  }

  removeStream(name: string) {
    this.streamStore.forEach((entry) => {
      if (entry.name === name) {
        entry.subscription.cancel()
      }
    })
    this.streamStore = this.streamStore.filter((entry) => entry.name !== name)
  }

  removeStreams(namespace: string) {
    this.streamStore.forEach((entry) => {
      if (entry.namespace === namespace) {
        entry.subscription.cancel()
      }
    })
    this.streamStore = this.streamStore.filter(
      (entry) => entry.namespace !== namespace
    )
  }

  closeAllStreams({
    namespace,
    name,
  }: { namespace?: string; name?: string } = {}) {
    this.streamStore.forEach((entry) => {
      if (
        (!namespace || entry.namespace === namespace) &&
        (!name || entry.name === name)
      ) {
        entry.subscription.cancel()
      }
    })
  }

  addReaction<T, B extends boolean>({
    whenThisChanges,
    thenRunThisCode,
    fireImmediately,
  }: {
    whenThisChanges: Parameters<typeof reaction<T, B>>[0]
    thenRunThisCode: Parameters<typeof reaction<T, B>>[1]
    fireImmediately?: B
  }) {
    this.disposers.push(
      reaction(whenThisChanges, thenRunThisCode, { fireImmediately })
    )
  }

  initialize() {}

  dispose() {
    this.disposers.forEach((dispose) => dispose())
    this.closeAllStreams()
    this.isDisposed = true
    return Cubit.callSuperDispose
  }
}
